import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Phone,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import { formatPropertyPriceParts, textLang } from "@triple-h/shared";
import { ArchitecturalBackdrop } from "../components/ArchitecturalBackdrop";
import { SearchPanel } from "../components/SearchPanel";
import { PropertyCard } from "../components/PropertyCard";
import { PropertyGridSkeleton } from "../components/PropertyGridSkeleton";
import { SectionHeading } from "../components/SectionHeading";
import { ErrorPanel } from "../components/ErrorPanel";
import { useCategories, useSite } from "../hooks/useSite";
import { useDiscovery } from "../hooks/useDiscovery";
import { useDocumentMeta } from "../lib/seo";
import {
  availability,
  fallbackSite,
  phoneHref,
  propertyDisplayTitle,
  viberHref,
} from "../lib/site";

export function HomePage() {
  const { data: site = fallbackSite } = useSite();
  const categories = useCategories();
  const discovery = useDiscovery();
  const d = discovery.data;
  const hero = d?.featured[0] || d?.latest.find((p) => p.coverUrl);
  const ctaProperty =
    d?.latest.find((property) => property.coverUrl && property.id !== hero?.id) ||
    d?.featured.find((property) => property.coverUrl && property.id !== hero?.id) ||
    hero;
  const price = hero ? formatPropertyPriceParts(hero) : null;
  useDocumentMeta({
    title: "ပြင်ဦးလွင် အိမ်ခြံမြေ အရောင်း | Triple H Myanmar",
    description:
      "ပြင်ဦးလွင်နှင့် အနီးစခန်းရှိ အိမ်၊ မြေ၊ စီးပွားရေးအဆောက်အအုံ အရောင်းများကို ဓာတ်ပုံ၊ တည်နေရာ၊ ဈေးနှုန်းနှင့်အတူ လေ့လာပါ။",
    image: hero?.coverUrl,
  });
  return (
    <>
      <section className="editorial-hero architectural-hero">
        <ArchitecturalBackdrop variant="elevation" className="hero-blueprint" />
        <span className="hero-edition" aria-hidden="true">01</span>
        <span className="hero-rail" aria-hidden="true">
          <span>ARCHITECTURAL PROPERTY · MYANMAR</span>
          <i />
        </span>
        <div className="container editorial-hero-grid">
          <div className="editorial-intro">
            <span className="eyebrow">TRIPLE H · PYIN OO LWIN</span>
            <h1 lang="my">
              ပြင်ဦးလွင်မှာ သင့်စိတ်ကြိုက်
              <br className="desktop-break" /> အိမ်ခြံမြေကို ရှာဖွေလိုက်ပါ။
            </h1>
            <p lang="my">
              အိမ်၊ မြေ နှင့် စီးပွားရေးအဆောက်အအုံများ။ တည်နေရာ၊ ဈေးနှုန်းနှင့်
              ဓာတ်ပုံများကို လေ့လာပြီး ကျွန်ုပ်တို့နှင့် တိုက်ရိုက်ဆက်သွယ်ပါ။
            </p>
            <div className="hero-property-search" id="property-search"><SearchPanel compact /></div>
            <div className="hero-search-contact"><a href={phoneHref(site.publicPhone)}><Phone aria-hidden="true" />{site.publicPhone}</a><Link to="/properties">View all properties <ArrowRight aria-hidden="true" /></Link></div>
          </div>
          <div className="editorial-visual">
            <span className="hero-visual-caption" aria-hidden="true">
              SELECTED · PROPERTY
            </span>
            {hero ? (
              <Link className="editorial-property" to={`/property/${hero.slug}`}>
                <img
                  src={hero.coverUrl!}
                  alt={propertyDisplayTitle(hero)}
                  width={1000}
                  height={750}
                  fetchPriority="high"
                />
                <div className="editorial-property-caption">
                  <span className="eyebrow">
                    {d?.featured.length
                      ? "SELECTED BY TRIPLE H"
                      : "FROM OUR LISTINGS"}
                  </span>
                  <h2 lang={textLang(propertyDisplayTitle(hero))}>
                    {propertyDisplayTitle(hero)}
                  </h2>
                  <p
                    className="hero-property-location"
                    lang={textLang(hero.locationText)}
                  >
                    <MapPin />
                    {hero.locationText}
                  </p>
                  <div>
                    <strong lang="my">{price?.primary}</strong>
                    <ArrowUpRight />
                  </div>
                  <small>{availability(hero).label} · View property</small>
                </div>
              </Link>
            ) : (
              <div
                className="editorial-property editorial-placeholder"
                aria-busy={discovery.isLoading}
              >
                <img
                  src="/brand/logo.png"
                  alt="Triple H Myanmar"
                  width={180}
                  height={180}
                />
              </div>
            )}
            <span className="hero-visual-index" aria-hidden="true">01</span>
          </div>
        </div>
      </section>
      {discovery.isError && (
        <div className="container">
          <ErrorPanel
            title="Properties could not be loaded"
            text="Please retry or call our team."
            onRetry={() => discovery.refetch()}
          />
        </div>
      )}
      {Boolean(d?.featured.length) && (
        <section className="section">
          <div className="container">
            <SectionHeading
              eyebrow="CONFIRMED WITH OUR TEAM"
              title="ရွေးချယ်ထားသော အိမ်ခြံမြေများ"
              intro="Availability has been confirmed by Triple H. Contact us for the latest price and a viewing."
              action={
                <Link
                  className="btn btn-text"
                  to="/properties?availability=available"
                >
                  View confirmed listings <ArrowRight />
                </Link>
              }
            />
            <div className="property-grid">
              {d!.featured.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          </div>
        </section>
      )}
      {Boolean(d?.locations.length) && (
        <section className="section location-section">
          <div className="container">
            <SectionHeading
              eyebrow="FIND YOUR NEIGHBORHOOD"
              title="တည်နေရာအလိုက် ရှာဖွေရန်"
              intro="Explore the locations recorded in our property listings."
            />
            <div className="location-links">
              {d!.locations.slice(0, 6).map((l) => (
                <Link
                  key={l.name}
                  to={`/properties?q=${encodeURIComponent(l.name)}`}
                >
                  <MapPin />
                  <span lang={textLang(l.name)}>{l.name}</span>
                  <small>{l.count} listings</small>
                  <ArrowUpRight />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <section className="section properties-section">
        <div className="container">
          <SectionHeading
            index="01"
            eyebrow="THE LATEST ADDITIONS"
            title="အသစ်တင်ထားသော အိမ်ခြံမြေများ"
            intro="Browse the latest listings. Availability is shown separately from the original publication date."
            action={
              <Link className="btn btn-outline" to="/properties">
                အားလုံး ကြည့်ရန် <ArrowRight />
              </Link>
            }
          />
          {discovery.isLoading ? (
            <PropertyGridSkeleton count={6} />
          ) : (
            <div className="property-grid property-grid-home">
              {d?.latest.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          )}
        </div>
      </section>
      {Boolean(categories.data?.some((c) => c.postCount)) && (
        <section className="section category-section">
          <div className="container">
            <SectionHeading
              index="02"
              eyebrow="A PLACE FOR EVERY PLAN"
              title="အိမ်ခြံမြေ အမျိုးအစားများ"
              intro="A quiet, image-led index of the property types currently published by Triple H."
            />
            <div className="real-category-grid">
              {categories.data
                ?.filter((c) => c.postCount)
                .sort((a, b) => (b.postCount || 0) - (a.postCount || 0))
                .map((c) => (
                  <Link key={c.id} to={`/properties?category=${c.slug}`}>
                    {d?.categories[c.id] && (
                      <img
                        src={d.categories[c.id]}
                        alt=""
                        loading="lazy"
                        width={600}
                        height={400}
                      />
                    )}
                    <span>
                      <strong>{c.name}</strong>
                      <small>{c.postCount} listings</small>
                      <ArrowUpRight />
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      )}
      <section className="integrity-section architectural-dark">
        <ArchitecturalBackdrop variant="plan" className="integrity-blueprint" />
        <img
          className="integrity-watermark"
          src="/brand/logo-mark.png"
          alt=""
          width={520}
          height={506}
          loading="lazy"
          aria-hidden="true"
        />
        <span className="integrity-index" aria-hidden="true">03</span>
        <div className="container integrity-grid">
          <div className="integrity-mark">
            <img
              src="/brand/logo-mark.png"
              alt="Triple H"
              width={110}
              height={107}
              loading="lazy"
            />
            <span className="eyebrow">REAL ESTATE WITH INTEGRITY</span>
            <p lang="my">ရိုးသားမှုကို အခြေခံသော အိမ်ခြံမြေဝန်ဆောင်မှု</p>
            <span className="integrity-coordinate" aria-hidden="true">
              PYIN OO LWIN · MYANMAR
            </span>
          </div>
          <div className="integrity-copy">
            <h2 lang="my">သိလိုသမျှ တိုက်ရိုက် မေးမြန်းပါ။</h2>
            <p>
              Compare actual property photographs, recorded prices and
              locations. We keep the original listing information available and
              identify details that need confirmation.
            </p>
            <div className="value-grid">
              <div className="value">
                <ShieldCheck />
                <div>
                  <h3>Clear property information</h3>
                  <p>
                    Verification dates appear only after our team confirms
                    availability.
                  </p>
                </div>
              </div>
              <div className="value">
                <MessageCircle />
                <div>
                  <h3>A real conversation</h3>
                  <p>
                    Discuss your budget, request details and arrange a viewing
                    directly with Triple H.
                  </p>
                </div>
              </div>
            </div>
            <Link className="btn btn-text" to="/about">
              About Triple H <ArrowRight />
            </Link>
          </div>
        </div>
      </section>
      <section className="section process-section architectural-process">
        <div className="container">
          <SectionHeading
            index="04"
            eyebrow="YOUR NEXT STEP"
            title="မေးမြန်းမှုမှ အိမ်ကြည့်ရန်အထိ"
            intro="A clear, three-step path from the first property you save to a confirmed viewing with our team."
          />
          <ol className="process-grid">
            <li>
              <span className="process-number">01</span>
              <h3 lang="my">အိမ်ခြံမြေ ရွေးချယ်ပါ</h3>
              <p>Compare location, photographs and asking price.</p>
            </li>
            <li>
              <span className="process-number">02</span>
              <h3 lang="my">အသေးစိတ် မေးမြန်းပါ</h3>
              <p>Confirm availability and discuss the details with our team.</p>
            </li>
            <li>
              <span className="process-number">03</span>
              <h3 lang="my">အိမ်ကြည့်ရန် ချိန်းဆိုပါ</h3>
              <p>Contact Triple H to arrange your next step.</p>
            </li>
          </ol>
        </div>
      </section>
      <section className="editorial-contact-cta">
        {ctaProperty?.coverUrl && (
          <img
            className="contact-cta-image"
            src={ctaProperty.coverUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
        )}
        <span className="contact-cta-scrim" aria-hidden="true" />
        <ArchitecturalBackdrop variant="elevation" className="contact-cta-blueprint" />
        <div className="container contact-cta-inner">
          <div className="contact-cta-copy">
            <span className="eyebrow eyebrow-light">LET’S TALK PROPERTY</span>
            <h2 lang="my">
              သင့်အတွက် သင့်တော်သော အိမ်ခြံမြေကို အတူရှာဖွေကြမယ်။
            </h2>
            <p>
              Call or message Triple H directly. We will help with availability,
              price and arranging the next viewing.
            </p>
          </div>
          <div className="contact-cta-actions">
            <a className="btn btn-accent" href={phoneHref(site.publicPhone)}>
              <Phone />
              {site.publicPhone}
            </a>
            {site.viberUrl && (
              <a
                className="btn btn-ghost-light"
                href={viberHref(site)}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle /> Viber
              </a>
            )}
            <Link className="btn btn-ghost-light" to="/contact">
              <span lang="my">ဆက်သွယ်ရန်</span> <ArrowRight />
            </Link>
          </div>
        </div>
        <span className="contact-cta-index" aria-hidden="true">05</span>
      </section>
    </>
  );
}
